import React from 'react';
import i18n from '@/i18n';
import { isChunkLoadError, reloadOnceForNewVersion } from '@/utils/chunkReload';

/**
 * The app had NO error boundary: any error thrown while rendering — a page
 * chunk missing after a deploy, or a bug in one component — unmounted the
 * whole tree and left a blank white page with no way back except knowing to
 * press F5. This catches it at the route level:
 *
 *   • a chunk that failed to load reloads once into the current build
 *     (see utils/chunkReload.js), which is the deploy case and needs no click;
 *   • anything else shows a short message with Reload / Home, so a crash in
 *     one page is a recoverable screen, not a dead tab.
 *
 * App passes the pathname as `resetKey`: navigating elsewhere (browser Back,
 * a nav link) clears the error instead of leaving every page on this screen.
 * Not a React `key` — that would remount the whole layout on every navigation.
 * Copy goes through the i18n instance directly — a class component cannot use
 * the hook, and the boundary must still render if a provider above it is the
 * thing that broke.
 */
const t = (key, fallback) => i18n.t(`common.appError.${key}`, { defaultValue: fallback });

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && !this.state.reloading && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error, info) {
    if (isChunkLoadError(error) && reloadOnceForNewVersion()) {
      this.setState({ reloading: true });
      return;
    }
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary]', error, info?.componentStack);
  }

  render() {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    return (
      <div
        role="alert"
        className="min-h-[60vh] flex items-center justify-center p-6"
      >
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-xl font-semibold">
            {reloading
              ? t('reloadingTitle', 'Loading the latest version…')
              : chunk
                ? t('updatedTitle', 'A new version is available')
                : t('title', 'Something went wrong on this page')}
          </h1>
          {!reloading && (
            <>
              <p className="text-sm text-muted-foreground">
                {chunk
                  ? t('updatedBody', 'This page was updated while it was open. Reload to continue.')
                  : t('body', 'Reload the page to try again. If it keeps happening, let us know what you were doing.')}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <button
                  type="button"
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
                >
                  {t('reload', 'Reload page')}
                </button>
                <a
                  href="/"
                  className="px-4 py-2 rounded-lg border text-sm font-medium"
                >
                  {t('home', 'Go to home page')}
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

export default AppErrorBoundary;
